import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { I18nextProvider } from 'react-i18next';
import './index.css'
import App from './App.tsx'
import i18n from './i18n';

/**
 * Suppress "Failed to execute 'removeChild' on 'Node'" DOM errors caused by
 * race conditions between Radix UI portal animations and React's reconciler
 * during state updates (e.g., polling on /documents), or by browser extensions
 * (Grammarly, LastPass, etc.) modifying React-managed DOM nodes.
 * This is a well-known React issue — the app state remains consistent.
 */
const REMOVE_CHILD_PATTERN = 'removeChild';
const NOT_A_CHILD_PATTERN = 'not a child';

function isRemoveChildError(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : String(error);
  return msg.includes(REMOVE_CHILD_PATTERN) ||
    (msg.includes('"Node"') && msg.includes(NOT_A_CHILD_PATTERN));
}

// React 19 onRecoverableError — handles commit-phase DOM errors from the reconciler
const onRecoverableError = (error: unknown) => {
  if (isRemoveChildError(error)) {
    console.warn('[React] Suppressed removeChild error (portal/animation race condition):',
      error instanceof Error ? error.message : error);
    return;
  }
  // Re-throw other recoverable errors so they surface normally
  console.error('[React] Recoverable error:', error);
};

// Global handler for uncaught DOM errors (browser extension interference)
window.addEventListener('error', (event: ErrorEvent) => {
  if (isRemoveChildError(event.message)) {
    event.preventDefault();
    console.warn('[DOM] Suppressed removeChild error (likely browser extension interference):',
      event.message);
  }
});

// Also catch unhandled promise rejections that may wrap the same DOM error
window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
  if (isRemoveChildError(event.reason?.message)) {
    event.preventDefault();
    console.warn('[DOM] Suppressed async removeChild error:', event.reason?.message);
  }
});

createRoot(document.getElementById('root')!, { onRecoverableError }).render(
  <StrictMode>
    <I18nextProvider i18n={i18n}>
      <App />
    </I18nextProvider>
  </StrictMode>,
)
